import { Card, CardContent, CardHeader, Tab, Tabs, Tooltip } from '@mui/material';
import { t } from 'i18next';
import { useState } from 'react';
import { ComponentLoader } from '../../components/ComponentLoader';
import { InventoryCategory } from '../models/InventoryCategory';
import { ChartContent } from './ChartContent';
import { ChartContent as ChartContentBlankState } from './blankStates/ChartContent';
import { TabChart as TabChartSkeleton } from './skeletons/TabChart';
import { HelpOutline } from '@mui/icons-material';

type Props = {
  title: string;
  categories: InventoryCategory[] | null;
  helpMessages?: Map<string, string>;
  loading: boolean;
  initialLoad: boolean;
  disableAnimation?: boolean;
};

const cardHeaderStyle = {
  borderBottom: 1,
  borderColor: 'divider',
  p: 0,
} as const;

export const TabChart = ({ title, helpMessages = new Map(), categories, loading, initialLoad, disableAnimation = false }: Props) => {
  const [activeCategory, setActiveCategory] = useState<number>(0);

  const handleActiveCategory = (idx: number) => {
    setActiveCategory(idx);
  };

  const selectedCategory = categories === null || activeCategory >= categories.length ? 0 : activeCategory;
  if (initialLoad && categories === null) {
    return <TabChartSkeleton />;
  }

  return (
    <Card elevation={2}>
      <ComponentLoader loading={loading}>
        <CardHeader
          sx={cardHeaderStyle}
          title={
            <Tabs variant="fullWidth" value={selectedCategory} onChange={(_, newValue) => handleActiveCategory(newValue as number)}>
              {(categories || []).map((category, idx) => {
                const help = helpMessages.get(category.getName()) ?? '';
                const helpText = help ? (
                  <Tooltip title={help}>
                    <HelpOutline fontSize={'inherit'} sx={{ verticalAlign: 'middle', ml: 1, mb: 0.375 }} />
                  </Tooltip>
                ) : (
                  ''
                );

                return (
                  <Tab
                    key={idx}
                    label={
                      <span>
                        {category.getName()} {helpText}
                      </span>
                    }
                  />
                );
              })}
              {(categories === null || categories.length === 0) && <Tab disabled />}
            </Tabs>
          }
        />
        <CardContent>
          {!categories || categories.length === 0 || !categories[selectedCategory] ? (
            <ChartContentBlankState title={title} />
          ) : (
            <ChartContent
              title={t('a11y.organization.dashboard.chartTitle', { chartName: categories[selectedCategory].getName() })}
              category={categories[selectedCategory]}
              disableAnimation={disableAnimation}
            />
          )}
        </CardContent>
      </ComponentLoader>
    </Card>
  );
};
